<template>
  <div class="home">
    <Header />

    <div v-if="loading" class="loading-block">
      <h2>The order is being processed.</h2>
      <h2>Just a few seconds left...</h2>
      <div class="loader-after-payment" />
    </div>

    <div v-else-if="orderSaved" class="intro">
      <h2>Thank you<br> for placing your order at</h2>
      <br><br>
      <h1>Copenhagen<br> Luggage Service</h1>

      <div class="first-block">
        <hr>
        <h2>For order confirmation<br> and next steps:</h2><br>
        <h3>Please check your email</h3>
        <div class="order-summary">
          <p v-if="confirmationPrice"><span>Price:</span> {{ confirmationPrice }}</p>
          <p v-if="confirmationEmail"><span>Email:</span> {{ confirmationEmail }}</p>
          <p v-if="orderNumber"><span>Order ID:</span> {{ orderNumber }}</p>
        </div>
        <img src="../assets/images/your_travel_we_carry.png">
        <div class="buttons-container btns-confirmation">
          <button class="btn-back" onclick="location.href='/#service'">Go back to Main page</button>
        </div>
      </div>

    </div>

    <div v-else class="missing-state">
      <h2>We could not complete your order automatically.</h2>
      <p>{{ processingError || 'Please contact support if the payment was captured.' }}</p>
      <div class="buttons-container btns-confirmation">
        <button class="btn-back" onclick="location.href='/#service'">Go back to Main page</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import moment from 'moment-timezone'
import Header from '@/components/Header'
import bookingDateValidation from '@/mixins/bookingDateValidation'
import { decodeStateFromQuery } from '@/utils/stateSerializer'
const { formatBookingDateForApi } = require('@/utils/bookingDate')

export default {
  name: 'AfterPayment',

  components: {
    Header
  },

  mixins: [bookingDateValidation],

  data: () => ({
    loading: true,
    stateRestored: false,
    orderSaved: false,
    orderNumber: '',
    processingError: ''
  }),

  computed: {
    ...mapGetters([
      'getOrderInfo', 'getStorageInfo', 'getCustomerInfo', 'getStateData', 'getPaymentIntent',
      'getAllBagsCount', 'priceCalculation', 'getStorageDays', 'getUrlPrices', 'getBookingContext'
    ]),
    shouldSkipPayment () {
      return Number(this.getUrlPrices.skipPayment) === 1 || Number(this.$route.query.skipPayment) === 1
    },
    isStorageOrder () {
      const storageInfo = this.getStorageInfo || {}
      return Boolean(storageInfo.numberOfBags || storageInfo.numberOfHeavyBags)
    },
    confirmationPrice () {
      const rawValue = this.isStorageOrder ? this.priceCalculation(false) : this.priceCalculation(true)
      const value = Number(rawValue)
      return Number.isFinite(value) && value > 0 ? `€${value.toFixed(2)}` : ''
    },
    confirmationEmail () {
      const customerInfo = this.getCustomerInfo || {}
      return (customerInfo.customerEmail || '').trim()
    }
  },

  methods: {
    ...mapMutations(['setState', 'setPaymentIntent']),
    buildEnhancedConversionData () {
      const customer = this.getCustomerInfo || {}
      const rawName = (customer.customerName || '').trim()
      const nameParts = rawName.split(/\s+/).filter(Boolean)
      const firstName = nameParts[0] || ''
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''
      const email = (customer.customerEmail || '').trim().toLowerCase()
      const phone = (customer.customerPhone || '').trim()
      const countryCode = (customer.customerCountryCode || '').trim()
      let phoneNumber = phone.startsWith('+') || !countryCode
        ? phone
        : `${countryCode}${phone}`

      phoneNumber = phoneNumber.replace(/[^\d+]/g, '')

      const addressField = (this.getOrderInfo && this.getOrderInfo.streetNumberPostal) || ''
      const apartmentField = (this.getOrderInfo && this.getOrderInfo.apartment) || ''
      const street = [addressField, apartmentField].filter(Boolean).join(' ')
      const enhancedData = {}

      if (email) enhancedData.email = email
      if (phoneNumber) enhancedData.phone_number = phoneNumber
      if (firstName || lastName || street) {
        enhancedData.address = {
          first_name: firstName,
          last_name: lastName,
          street
        }
      }

      return Object.keys(enhancedData).length ? enhancedData : null
    },
    pushPurchaseDataLayer () {
      if (typeof window === 'undefined') return
      if (this.shouldSkipPayment) return

      const hasStorageBags = !!(this.getStorageInfo.numberOfBags || this.getStorageInfo.numberOfHeavyBags)
      const isArrival = !hasStorageBags
      const valueRaw = isArrival ? this.priceCalculation(true) : this.priceCalculation(false)
      const value = Number(valueRaw) || 0
      const sessionId = this.$route && this.$route.query ? this.$route.query.session_id : ''
      const transactionId = this.getPaymentIntent || sessionId

      if (!transactionId || value <= 0) return

      const purchaseKey = `purchase_pushed_${transactionId}`
      try {
        if (window.sessionStorage && window.sessionStorage.getItem(purchaseKey)) return
      } catch (err) {
        // Ignore storage access errors
      }

      const itemName = isArrival ? 'Luggage Transfer' : 'Luggage Storage'
      const itemId = isArrival ? 'TRANSFER' : 'STORAGE'
      const isProd = process.env.NODE_ENV === 'production'
      const enhancedData = isProd ? this.buildEnhancedConversionData() : null

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: transactionId,
          value,
          currency: 'EUR',
          items: [
            {
              item_id: itemId,
              item_name: itemName,
              price: value,
              quantity: 1
            }
          ]
        },
        ...(enhancedData ? { enhanced_conversion_data: enhancedData } : {})
      })

      try {
        if (window.sessionStorage) {
          window.sessionStorage.setItem(purchaseKey, '1')
        }
      } catch (err) {
        // Ignore storage access errors
      }
    },
    formatDate (date) {
      return formatBookingDateForApi(date)
    },
    restoreStateFromQuery () {
      const encodedState = this.$route.query.state
      if (!encodedState) {
        return false
      }

      const decodedState = decodeStateFromQuery(encodedState)
      if (!decodedState) {
        return false
      }

      this.setState(decodedState)
      return true
    },
    async fetchPaymentIntent () {
      const sessionId = this.$route.query.session_id
      if (!sessionId) {
        console.warn('No session_id in query params')
        return
      }

      try {
        const response = await fetch(`${process.env.VUE_APP_PROD_MODE === 'true'
          ? process.env.VUE_APP_API_SERVER
          : 'http://localhost:3100'}/stripe/session?session_id=${sessionId}`)
        
        const data = await response.json()
        if (data.payment_intent) {
          this.setPaymentIntent(data.payment_intent)
        }
      } catch (err) {
        console.error('Failed to fetch payment intent:', err)
      }
    },
    async getInfoFromVuex () {
      let refValue = document.cookie.split(';') || []

      if (refValue.length) {
        refValue = refValue.find(c => c.includes('ref=')) || ''
        if (refValue) {
          refValue = refValue.split('=')[1]
        }
      }

      const dateNow = new Date()
      const objectMomentTimeZone = moment.tz('Europe/Copenhagen')
      const orderDate = objectMomentTimeZone.format('DD.MM.YYYY')
      const orderTime = objectMomentTimeZone.format('HH:mm:ss')
      const totalObj = {}

      totalObj.customerInfo = {
        ...this.getCustomerInfo,
        ref: refValue
      }

      totalObj.additionalInfo = this.getStateData.additionalInfo

      if (!this.getStorageInfo.numberOfBags && !this.getStorageInfo.numberOfHeavyBags) {
        totalObj.orderInfo = this.getOrderInfo
        totalObj.additionalInfo.priceInfo = this.priceCalculation(true)
        totalObj.additionalInfo.totalBags = this.getAllBagsCount(true)
        totalObj.orderInfo.arrivalDate = this.formatDate(this.getOrderInfo.arrivalDate)
        totalObj.orderInfo.orderDate = orderDate
        totalObj.orderInfo.orderTime = orderTime
        totalObj.orderInfo.orderChangedDate = Date.parse(dateNow)
        totalObj.orderInfo.paymentId = this.getPaymentIntent
        totalObj.orderInfo.noprintself = false
        totalObj.orderInfo.isOpen = true
      } else {
        totalObj.storageInfo = this.getStorageInfo
        totalObj.additionalInfo.priceInfo = this.priceCalculation(false)
        totalObj.additionalInfo.totalBags = this.getAllBagsCount(false)
        totalObj.storageInfo.storageDateFrom = this.formatDate(this.getStorageInfo.storageDateFrom)
        totalObj.storageInfo.storageDateEnd = this.formatDate(this.getStorageInfo.storageDateEnd)
        totalObj.additionalInfo.storageDays = this.getStorageDays
        console.log('****sto days = ' + this.getStorageDays)
        totalObj.storageInfo.orderDate = orderDate
        totalObj.storageInfo.orderTime = orderTime
        totalObj.storageInfo.paymentId = this.getPaymentIntent
      }

      totalObj.additionalInfo.paidInfo = '' // (this.getUrlPrices.skipPayment === 1) ? 'Not paid' : 'Paid'
      totalObj.bookingEligibility = this.getBookingEligibilityPayload()
      totalObj.checkoutSessionId = this.$route.query.session_id || ''
      console.log('payment ' + totalObj.additionalInfo.paidInfo)

      try {
        this.loading = true

        const response = await fetch(`${process.env.VUE_APP_PROD_MODE === 'true'
          ? process.env.VUE_APP_API_SERVER
          : 'http://localhost:3100'}/order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(totalObj)
        })
        let orderResponse = null
        try {
          orderResponse = await response.json()
        } catch (parseErr) {
          // Keep compatibility with old API responses that return plain text
        }

        if (!response.ok) {
          throw new Error(`Failed to save order. Status: ${response.status}`)
        }

        this.orderNumber = orderResponse && (orderResponse.orderNumber || orderResponse.orderId)
          ? (orderResponse.orderNumber || orderResponse.orderId)
          : ''

        if (!this.orderNumber) throw new Error('Order number was not returned.')
        this.orderSaved = true
        this.pushPurchaseDataLayer()
        this.$gtm.trackEvent({
          event: 'afterPayment',
          category: 'order',
          action: 'Event after successful payment',
          label: 'Order fulfilled',
          value: this.getPaymentIntent
        })
      } catch (err) {
        this.processingError = 'The payment was received, but the order could not be saved. Please contact support.'
      } finally {
        this.loading = false
      }
    }
  },

  async created () {
    const hasState = this.restoreStateFromQuery()
    this.stateRestored = hasState
    this.orderNumber = (this.$route.query.orderNumber || '').toString().trim()

    if (!hasState) {
      this.processingError = 'We could not find the order details. Please contact support if the payment was captured.'
      this.loading = false
      return
    }

    if (this.shouldSkipPayment) {
      this.orderSaved = Boolean(this.orderNumber)
      this.processingError = this.orderSaved
        ? ''
        : 'The order number is missing. Please contact support.'
      this.loading = false
      return
    }

    try {
      await this.fetchPaymentIntent()
      await this.getInfoFromVuex()
    } catch (error) {
      this.processingError = 'The payment was received, but the order could not be saved. Please contact support.'
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped lang="scss">
   h2 {
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-weight: bold;
    font-size: 24px;
    letter-spacing: 0.1rem;
    text-align: center;
    color: #53565A;
  }

  img {
    width: 60%;
  }

  .btns-confirmation {
    display: flex;
    justify-content: center;
    margin-top: 40px;
  }

  .btn-back {
    display: flex;
    align-items: center;
    height: auto;
    width: auto;
    left: 50%;
    padding: 8px;
  }

  .order-summary {
    max-width: 540px;
    margin: 12px auto 18px;
    text-align: center;
    color: #53565A;
  }

  .order-summary p {
    margin: 6px 0;
    word-break: break-word;
  }

  .order-summary span {
    font-weight: 700;
  }

  .loading-block {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    padding: 50px 0;
    height: 100vh;
  }

  .loader-after-payment {
    border: 7px solid #00a9e0; /* Light grey */
    border-top: 7px solid #f3f3f3; /* Blue */
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
  }

  .missing-state {
    min-height: 70vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding: 40px 20px;
    text-align: center;
  }
</style>
